import { PlayCircleOutlined } from "@ant-design/icons";
import { Button, Space, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";

import { useAxiosPrivate } from "../../hooks/useAxiosPrivate";
import { useExceptionHandler } from "../../hooks/useExceptionHandler";
import { useAlertStore } from "../../store/alert-store";
import { useCustomToolStore } from "../../store/custom-tool-store";
import { useSessionStore } from "../../store/session-store";
import usePostHogEvents from "../../hooks/usePostHogEvents";
import { TokenUsage } from "../../components/custom-tools/token-usage/TokenUsage";
import usePromptOutput from "../../hooks/usePromptOutput";

function RunSinglePassBtn() {
  const {
    details,
    selectedDoc,
    defaultLlmProfile,
    updateCustomTool,
    summarizeIndexStatus,
    isSinglePassExtractLoading,
    isPublicSource,
  } = useCustomToolStore();
  const { sessionDetails } = useSessionStore();
  const { setAlertDetails } = useAlertStore();
  const axiosPrivate = useAxiosPrivate();
  const handleException = useExceptionHandler();
  const { setPostHogCustomEvent } = usePostHogEvents();
  const [tokenUsageKey, setTokenUsageKey] = useState(null);
  const { updatePromptOutputState } = usePromptOutput();

  useEffect(() => {
    setTokenUsageKey(
      `single_pass__${defaultLlmProfile}__${selectedDoc?.document_id}`
    );
  }, [selectedDoc, defaultLlmProfile]);

  const handleRunSinglePassExtraction = () => {
    try {
      setPostHogCustomEvent("ps_prompt_run", {
        info: "Clicked on 'Run Prompt' button (Single Pass)",
      });
    } catch (err) {
      // If an error occurs while setting custom posthog event, ignore it and continue
    }

    if (!defaultLlmProfile) {
      setAlertDetails({
        type: "error",
        content: "LLM Profile is not selected",
      });
      return;
    }

    if (!selectedDoc) {
      setAlertDetails({
        type: "error",
        content: "Document not selected",
      });
      return;
    }

    const isSummaryIndexed = [...summarizeIndexStatus].find(
      (indexStatus) =>
        indexStatus?.docId === selectedDoc?.document_id &&
        indexStatus?.isIndexed === true
    );

    if (
      !isSummaryIndexed &&
      details?.summarize_as_source &&
      details?.summarize_llm_profile
    ) {
      // Summary needs to be indexed before running the prompt
      updateCustomTool({ isSinglePassExtractLoading: false });
      setAlertDetails({
        type: "error",
        content: `Summary needs to be indexed before running the prompt - ${selectedDoc?.document_name}. Index it through Manage Documents`,
      });
      return;
    }

    updateCustomTool({ isSinglePassExtractLoading: true });
    handleSinglePassExtractionApi(selectedDoc?.document_id)
      .then((res) => {
        const data = res?.data || [];
        updatePromptOutputState(data, true);
      })
      .catch((err) => {
        setAlertDetails(
          handleException(err, "Failed to run single pass extraction")
        );
      })
      .finally(() => {
        updateCustomTool({ isSinglePassExtractLoading: false });
      });
  };

  const handleSinglePassExtractionApi = async (docId) => {
    const body = {
      document_id: docId,
    };

    const requestOptions = {
      method: "POST",
      url: `/api/v1/unstract/${sessionDetails?.orgId}/prompt-studio/single-pass-extraction/${details?.tool_id}`,
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
        "Content-Type": "application/json",
      },
      data: body,
    };

    return axiosPrivate(requestOptions)
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  };

  return (
    <Space size={0}>
      <Tag color="#2db7f5">
        Token:{" "}
        <TokenUsage
          tokenUsageId={tokenUsageKey}
          isLoading={isSinglePassExtractLoading}
        />
      </Tag>
      <Tooltip title="SinglePass Extraction">
        <Button
          onClick={handleRunSinglePassExtraction}
          loading={isSinglePassExtractLoading}
          icon={<PlayCircleOutlined />}
          disabled={isPublicSource}
        />
      </Tooltip>
    </Space>
  );
}

export { RunSinglePassBtn };
